import {hash} from 'ohash';

// Deduplicates concurrent token refresh requests — only one refresh runs at a time
let _pendingTokenRefresh = null;

export async function useApi(url, fetchOptions = {}, options = {}) {
	const lang = useLang();

	// cache key. Important: must be declared before token to avoid hash mismatch
	let key;
	// Check cache: true = cache everywhere, "client" = cache only on client
	if ((options?.cache === true || (options?.cache === 'client' && import.meta.client)) && !(fetchOptions?.body instanceof FormData)) {
		key = options?.key ? hash(['api-fetch', url, options.key, lang.get()]) : hash(['api-fetch', url, fetchOptions, lang.get()]);
		if (options?.dataKey) key = options.dataKey;

		// get cached data
		const cachedData = useNuxtData(key);
		if (cachedData?.data?.value) {
			return options?.fetchMode == 'reactive' ? cachedData : cachedData.data.value;
		}
	}

	const nuxtApp = useNuxtApp();
	const t = useToken();
	const config = useAppConfig();
	let authToken = fetchOptions?.token ? fetchOptions.token : t.get();

	// If token is not available on server, fetch it before making any API calls
	if (import.meta.server && !authToken && !url.startsWith('/api/nuxtapi/')) {
		if (!nuxtApp._st) {
			nuxtApp._st = $fetch('/api/nuxtapi/token').catch(e => {
				return null;
			});
		}
		authToken = await nuxtApp._st;
	}

	// Get host from url if multidomain is enabled. Otherwise return default host.
	const host = () => {
		let host = config.host;
		if (config.multiHost?.length) {
			const h = useRequestURL().host;
			if (h) {
				const item = config.multiHost.find(item => h.includes(item.alias));
				if (item) {
					host = item.host;
					nuxtApp.$host = host;
				}
			}
		}
		return host;
	};

	let baseURL = url.startsWith('/api/nuxtapi/') ? '' : host();

	const fetchConfig = {
		headers: {
			'Authorization': 'Bearer ' + authToken,
			'X-Base-Host': host(),
			'X-Base-Lang': lang.get(),
		},
		baseURL: baseURL,
		key: key,
		...fetchOptions,
	};

	// add lang to body if POST method
	if ((fetchOptions?.method == 'POST' || fetchOptions?.method == 'PUT') && !(fetchOptions?.body instanceof FormData)) {
		if (!fetchOptions?.body) fetchConfig.body = {};
		let appendLang = true;
		if (fetchOptions.method == 'POST' && (!config.fetchApi?.post?.appendLang || options?.appendLang === false)) appendLang = false;
		if (fetchOptions.method == 'PUT' && (!config.fetchApi?.put?.appendLang || options?.appendLang === false)) appendLang = false;
		if (fetchConfig.body && options?.cache == false) fetchConfig.body.ignoreCache = true;
		if (appendLang) fetchConfig.body.lang = lang.get();
	}

	// add lang to url if GET method and appendLang
	if ((!fetchOptions?.method || fetchOptions?.method == 'GET') && options?.appendLang) {
		if (!url.includes('?')) {
			url += '?lang=' + lang.get();
		} else if (!url.includes('lang=')) {
			url += '&lang=' + lang.get();
		}
	}

	const handleError = async status => {
		if (!status) return;
		if (status === 401 || status === 503) {
			if (import.meta.client && status === 401) {
				t.removeToken();
				return window.location.reload();
			}
			throw createError({
				statusCode: status,
				statusMessage: `Error ${status}`,
			});
		}
	};

	// use default Nuxt useFetch
	if (options?.fetchMode == 'reactive') {
		const res = await useFetch(url, fetchConfig);
		await handleError(res.error?.value?.statusCode);
		return res;
	}

	const processFetchResult = res => {
		let data = res;
		if (fetchOptions?.transform) data = fetchOptions.transform(res);
		if (key && (options?.cache === true || (options?.cache === 'client' && import.meta.client))) {
			nuxtApp.payload.data[key] = data;
		}
		return data;
	};

	// use vanilla ofetch by default
	try {
		return processFetchResult(await $fetch(url, fetchConfig));
	} catch (error) {
		// On client 401: refresh token once (deduplicated) and retry instead of reloading
		if (import.meta.client && error.status === 401) {
			if (!_pendingTokenRefresh) {
				_pendingTokenRefresh = t.generate().finally(() => {
					_pendingTokenRefresh = null;
				});
			}
			try {
				const newToken = await _pendingTokenRefresh;
				if (newToken) {
					const retryConfig = {
						...fetchConfig,
						headers: {
							...fetchConfig.headers,
							'Authorization': 'Bearer ' + newToken,
						},
					};
					return processFetchResult(await $fetch(url, retryConfig));
				}
			} catch (_) {
				// Refresh or retry failed — return error data without reload
			}
			return {
				...error.data,
				statusCode: error.status,
			};
		}
		await handleError(error.status);
		return {
			...error.data,
			statusCode: error.status,
		};
	}
}
